import { Injectable } from '@angular/core';

import {
  isCrawler,
  getCookie,
  setCookie,
  uniqId
} from '@core/helpers';

const STORAGE_KEY = 'csrf_token';
const KEY_LENGTH = 32;

type ConsistencyResult = {
  isConsistent: boolean;
  value: string|undefined;
};

// this works faster than regexp test
// https://stackoverflow.com/a/25352300
function isAlphaNumeric(str: string): boolean {
  for (let i = 0, len = str.length; i < len; i++) {
    const code = str.charCodeAt(i);

    if (!(code > 47 && code < 58) && // numeric (0-9)
        !(code > 64 && code < 91) && // upper alpha (A-Z)
        !(code > 96 && code < 123)) // lower alpha (a-z)
    {
      return false;
    }
  }

  return true;
}

@Injectable({
  providedIn: 'root'
})
export class MarkerService {
  private _marker?: string;

  private cookiesSupported!: boolean;
  private localStorageSupported!: boolean;
  private sessionStorageSupported!: boolean;

  constructor() {
    this.checkSupportedStores();
  }

  get marker(): string {
    return this._marker || uniqId(KEY_LENGTH);
  }

  ensureMarker(): void {
    if(!this.isSupported()) {
      return;
    }

    const result = this.checkConsistence(STORAGE_KEY);

    if(!result.isConsistent) {
      this._marker = result.value || uniqId(KEY_LENGTH);

      this.setValue(STORAGE_KEY, this._marker);
    } else {
      this._marker = result.value!;
    }
  }

  private checkConsistence(key: string, defaultValue = '-'): ConsistencyResult {
    const storedValues: string[] = [];
    let result = '';

    if(this.cookiesSupported) {
      const value: string = getCookie(key) || defaultValue;

      storedValues.push(value);
    }

    if(this.localStorageSupported) {
      const value: string = window.localStorage.getItem(key) || defaultValue;
      
      storedValues.push(value);
    }

    if(this.sessionStorageSupported) {
      const value: string = window.sessionStorage.getItem(key) || defaultValue;

      storedValues.push(value);
    }

    const stats: Record<string, number> = {};
    let numMax = 0;

    for(let j = 0; j < storedValues.length; j++) {
      const value: string = storedValues[j];

      if(!stats[value]) {
        stats[value] = 0;
      }

      stats[value]++;
      
      if(stats[value] > numMax && value !== defaultValue) {
        numMax = stats[value];
        result = value;
      }
    }

    const isValidResult: boolean = isAlphaNumeric(result) && result.length === KEY_LENGTH;

    return {
      isConsistent: stats[result] === storedValues.length && isValidResult,
      value: isValidResult ? result : undefined
    };
  }

  private setValue(key: string, value: string): void {
    if(this.cookiesSupported) {
      setCookie(key, value, 30);
    }

    if(this.localStorageSupported) {
      window.localStorage.setItem(key, value);
    }

    if(this.sessionStorageSupported) {
      window.sessionStorage.setItem(key, value);
    }
  }

  private checkSupportedStores(): void {
    this.cookiesSupported = typeof document === 'object' &&
                                      typeof document.cookie === 'string';
    
    this.localStorageSupported = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
    this.sessionStorageSupported = typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
  }

  private isSupported(): boolean {
    return (this.cookiesSupported ||
            this.localStorageSupported ||
            this.sessionStorageSupported
    ) && !isCrawler;
  }
}